import { renderPageShell } from '../lib/page-shell';
import { fetchCatalog, fetchInventory, equipItem, type CatalogItem, type Rarity } from '../lib/catalog';
import { fetchMe } from '../lib/session';
import type { RouteHandler } from '../lib/router';

// The C009 general catalogue's loadout screen — separate from, and additive
// to, the legacy timer-theme "Customise" screen (`customise.ts`). See
// docs/2026-08-07_cosmetics_store_handover.md for why two systems exist.
//
// Deliberately owned-only (owner decision, 2026-08-10): this page is "what
// you actually have," not a catalogue browser. Achievement and level unlocks
// are earned progression — see `achievements.ts` for the full ladder,
// including what's still locked. Anything buyable lives in `store.ts` only.

// Matches each cosmetic type's locked canvas shape in the Cosmetic Factory
// tool (server/src/services/templates.ts there) — emblem/rank_frame/
// timer_face are square, name_plate and call_sign_plate are 4:1, profile_banner
// is 1600:480. Slots not listed (e.g. this project's own custom types) fall
// back to square.
const ASPECT_RATIO_BY_SLOT: Record<string, string> = {
  emblem: '1 / 1',
  rank_frame: '1 / 1',
  timer_face: '1 / 1',
  name_plate: '1024 / 256',
  // The game's catalog slot is "call_sign" (text + style) — the wide plate
  // backdrop is "call_sign_plate" in Cosmetic Factory's own asset-type
  // naming, not a separate game slot.
  call_sign: '1600 / 400',
  profile_banner: '1600 / 480',
};

const SLOT_LABELS: Record<string, string> = {
  call_sign: 'Call Sign',
  emblem: 'Emblem',
  name_plate: 'Name Plate',
  profile_banner: 'Profile Banner',
  timer_face: 'Timer Face',
};

function slotLabel(slot: string): string {
  return SLOT_LABELS[slot] ?? slot.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export const renderInventory: RouteHandler = (container, navigate) => {
  const body = renderPageShell(container, navigate, 'account', {
    title: 'INVENTORY',
    subtitle: "What you actually own, one item equipped per slot. Still working on unlocks? See Achievements. Want to buy something? See Store.",
    body: `<div id="inventory-mount"><p class="page-body__stub">Loading…</p></div>`,
  });

  const mount = body.querySelector<HTMLDivElement>('#inventory-mount')!;

  async function render() {
    const me = await fetchMe();
    if (!me) {
      mount.innerHTML = `
        <div class="handoff">
          <p class="handoff__label">Sign in required</p>
          <h2>Your inventory is tied to your account</h2>
          <button class="button button--primary" type="button" data-action="account">Go to account</button>
        </div>
      `;
      mount.querySelector<HTMLButtonElement>('[data-action="account"]')?.addEventListener('click', () => navigate('account'));
      return;
    }

    const [catalog, inventory] = await Promise.all([fetchCatalog(), fetchInventory()]);
    const rarityById = new Map<string, Rarity>(catalog.rarities.map((r) => [r.id, r]));
    const owned = new Set(inventory.owned);

    const ownedItems = catalog.items.filter((item) => item.active && owned.has(item.id));
    const slots = [...new Set(ownedItems.map((item) => item.slot))];
    const bySlot = new Map<string, CatalogItem[]>();
    for (const slot of slots) {
      const items = ownedItems
        .filter((item) => item.slot === slot)
        .sort((a, b) => (rarityById.get(a.rarity)?.rank ?? 0) - (rarityById.get(b.rarity)?.rank ?? 0) || a.name.localeCompare(b.name));
      bySlot.set(slot, items);
    }

    if (ownedItems.length === 0) {
      mount.innerHTML = `
        <div class="handoff">
          <p class="handoff__label">Nothing owned yet</p>
          <h2>Play to earn your first cosmetics</h2>
          <div class="hero-actions">
            <button class="button button--primary" type="button" data-action="achievements">See achievements</button>
            <button class="button button--secondary" type="button" data-action="store">Visit store</button>
          </div>
        </div>
      `;
      mount.querySelector<HTMLButtonElement>('[data-action="achievements"]')?.addEventListener('click', () => navigate('achievements'));
      mount.querySelector<HTMLButtonElement>('[data-action="store"]')?.addEventListener('click', () => navigate('store'));
      return;
    }

    mount.innerHTML = slots
      .map(
        (slot) => `
          <h2 class="history-heading">${slotLabel(slot)}</h2>
          <div class="cosmetics-grid">
            ${(bySlot.get(slot) ?? [])
              .map((item) =>
                renderItemCard(item, {
                  equipped: inventory.equipped[slot] === item.id,
                  rarity: rarityById.get(item.rarity),
                }),
              )
              .join('')}
          </div>
        `,
      )
      .join('');

    mount.querySelectorAll<HTMLButtonElement>('[data-action="equip"]').forEach((button) => {
      button.addEventListener('click', async () => {
        const itemId = button.dataset.itemId!;
        button.disabled = true;
        try {
          await equipItem(itemId);
          render();
        } catch (error) {
          button.disabled = false;
          button.textContent = error instanceof Error ? error.message : 'Could not equip.';
        }
      });
    });
  }

  function renderItemCard(item: CatalogItem, state: { equipped: boolean; rarity: Rarity | undefined }): string {
    const rarityColor = state.rarity?.color ?? '#9aa0a6';
    const rarityName = state.rarity?.name ?? item.rarity;
    // Cosmetic Factory art, once it exists for this item (config.image / asset_ref
    // — see docs/VISUAL_AXES.md in the Cosmetic Factory tool). Most items don't
    // have it yet, so this falls back to the plain rarity-colour swatch.
    const artUrl = typeof item.config.image === 'string' ? item.config.image : null;
    // Real aspect ratio per cosmetic type (Cosmetic Factory's own locked
    // canvas sizes — server/src/services/templates.ts in that tool) so art
    // renders in its native shape instead of being squashed into one fixed
    // rectangle for every slot.
    const aspectRatio = ASPECT_RATIO_BY_SLOT[item.slot] ?? '1 / 1';
    const swatch = artUrl
      ? `<div class="cosmetic-card__swatch cosmetic-card__swatch--art" style="background-image:url('${artUrl}'); border-color:${rarityColor}; aspect-ratio:${aspectRatio}; height:auto;"></div>`
      : `<div class="cosmetic-card__swatch" style="background:${rarityColor}22; border-color:${rarityColor};">
          <span style="background:${rarityColor};"></span>
        </div>`;

    return `
      <div class="choice-card cosmetic-card">
        ${swatch}
        <strong>${item.name}</strong>
        <small>${item.description}</small>
        <small style="color:${rarityColor};">${rarityName}</small>
        <button
          class="button ${state.equipped ? 'button--primary' : 'button--secondary'}"
          type="button"
          data-action="${state.equipped ? '' : 'equip'}"
          data-item-id="${item.id}"
          ${state.equipped ? 'disabled' : ''}
        >${state.equipped ? 'Equipped' : 'Equip'}</button>
      </div>
    `;
  }

  render();
};
